import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ListingCard } from '../components/ListingCard';
import { listingsApi, categoriesApi, citiesApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowRight, Loader2, Plus, MapPin, Flame, Clock,
  Smartphone, Car, Shirt, Home, Sofa, Scissors, 
  UtensilsCrossed, Calendar, Briefcase, Wrench, Users, ChevronRight
} from 'lucide-react';

const iconMap = {
  Smartphone: Smartphone,
  Car: Car,
  Shirt: Shirt,
  Home: Home,
  Sofa: Sofa,
  Scissors: Scissors,
  UtensilsCrossed: UtensilsCrossed,
  Calendar: Calendar,
  Briefcase: Briefcase,
  Wrench: Wrench,
  Users: Users
};

export default function HomePage() {
  const [featuredListings, setFeaturedListings] = useState([]);
  const [recentListings, setRecentListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuredRes, recentRes, categoriesRes, citiesRes] = await Promise.all([
          listingsApi.getAll({ limit: 4 }).catch(() => ({ data: [] })),
          listingsApi.getLatest(8),
          categoriesApi.getAll(),
          citiesApi.getAll()
        ]);
        setFeaturedListings(featuredRes.data.slice(0, 4));
        setRecentListings(recentRes.data);
        setCategories(categoriesRes.data);
        setCities(citiesRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4">
              Marketplace for Habesha in Canada
            </p>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-5"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Buy, Sell &{' '}
              <span className="text-primary">Connect</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-500 mb-8 leading-relaxed">
              The trusted marketplace for the Ethiopian & Eritrean community across Canada.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                size="lg" 
                onClick={() => navigate('/browse')}
                className="gap-2 bg-primary hover:bg-[#097549] text-white font-semibold px-8"
                data-testid="browse-listings-btn"
              >
                Browse Listings
                <ArrowRight className="h-4 w-4" />
              </Button>
              {isAuthenticated ? (
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/create-listing')}
                  className="gap-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-semibold"
                  data-testid="post-listing-btn"
                >
                  <Plus className="h-4 w-4" />
                  Post Free Ad
                </Button>
              ) : (
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/register')}
                  className="gap-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-semibold"
                  data-testid="get-started-btn"
                >
                  Get Started Free
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-12 sm:py-16" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 
              className="text-2xl sm:text-3xl font-semibold text-gray-900"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Browse Categories
            </h2>
            <Link 
              to="/browse"
              className="text-sm font-medium text-primary hover:text-[#097549] flex items-center gap-1"
            >
              View All <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {categories.slice(0, 12).map((category) => {
              const Icon = iconMap[category.icon] || Wrench;
              return (
                <Link
                  key={category.id}
                  to={`/browse?category=${category.id}`}
                  className="group flex flex-col items-center justify-center rounded-xl bg-white p-5 ring-1 ring-gray-100 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 hover:ring-gray-200"
                  data-testid={`category-${category.id}`}
                >
                  <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center mb-3 transition-colors group-hover:bg-primary group-hover:text-white text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-medium text-sm text-gray-900 text-center">{category.name}</h3>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Listings Section */}
      {featuredListings.length > 0 && (
        <section className="py-12 sm:py-16 bg-gray-50/60" data-testid="featured-listings-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-2.5">
                <Flame className="h-5 w-5 text-[#E53935]" />
                <h2 
                  className="text-2xl sm:text-3xl font-semibold text-gray-900"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Featured Listings
                </h2>
              </div>
              <Link 
                to="/browse" 
                className="text-sm font-medium text-primary hover:text-[#097549] flex items-center gap-1"
              >
                See All <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {featuredListings.map((listing, index) => (
                <div key={listing.id} className={`animate-fade-in stagger-${(index % 4) + 1}`}>
                  <ListingCard listing={listing} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recent Listings Section */}
      <section className="py-12 sm:py-16" data-testid="recent-listings-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2.5">
              <Clock className="h-5 w-5 text-primary" />
              <h2 
                className="text-2xl sm:text-3xl font-semibold text-gray-900"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Recent Listings
              </h2>
            </div>
            <Link 
              to="/browse" 
              className="text-sm font-medium text-primary hover:text-[#097549] flex items-center gap-1"
              data-testid="view-all-link"
            >
              View All <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : recentListings.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {recentListings.map((listing, index) => (
                <div key={listing.id} className={`animate-fade-in stagger-${(index % 4) + 1}`}>
                  <ListingCard listing={listing} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-xl ring-1 ring-gray-100">
              <p className="text-gray-500 mb-4">No listings yet. Be the first to post!</p>
              {isAuthenticated ? (
                <Button onClick={() => navigate('/create-listing')} className="bg-primary hover:bg-[#097549]" data-testid="empty-post-btn">
                  Post a Listing
                </Button>
              ) : (
                <Button onClick={() => navigate('/register')} className="bg-primary hover:bg-[#097549]" data-testid="empty-signup-btn">
                  Sign Up to Post
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Browse by City Section */}
      <section className="py-12 sm:py-16 bg-gray-50/60" data-testid="cities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2.5">
              <MapPin className="h-5 w-5 text-primary" />
              <h2 
                className="text-2xl sm:text-3xl font-semibold text-gray-900"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Browse by City
              </h2>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {cities.map((city) => (
              <Link
                key={city.id}
                to={`/browse?search=${city.name}`}
                className="px-5 py-2.5 bg-white ring-1 ring-gray-200 rounded-lg hover:ring-primary/40 hover:text-primary transition-all duration-200 flex items-center gap-2"
                data-testid={`city-${city.id}`}
              >
                <MapPin className="h-4 w-4 text-gray-400" />
                <span className="font-medium text-sm text-gray-900">{city.name}</span>
                <span className="text-xs text-gray-400">{city.province}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {!isAuthenticated && (
        <section className="py-14 sm:py-20" data-testid="cta-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 
              className="text-3xl sm:text-4xl font-semibold text-gray-900 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Ready to Join HabeshaTown?
            </h2>
            <p className="text-gray-500 mb-8 max-w-xl mx-auto">
              Post free listings, connect with buyers, and grow your business in the Habesha community.
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate('/register')}
              className="gap-2 bg-primary hover:bg-[#097549] text-white font-semibold px-8"
              data-testid="cta-signup-btn"
            >
              Create Free Account
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
